import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Tooltip,
  InputNumber,
  Row,
  Col,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import {
  CreateCustomerRequest,
  default as CustomerResponse,
} from '../../model/Customer.model';
import customerService from '../../service/customer-service/Customer.service';
import partnerService from '../../service/partner-service/Partner.service';
import { Partner } from '../../model/Partner.model';
import dayjs from 'dayjs';

const { TextArea } = Input;

interface EditCustomerModalProps {
  customer: CustomerResponse;
  onSuccess: () => void;
}

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  customer,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
    customer.partnerId || null
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        name: customer.name,
        contactInfo: customer.contactInfo,
        email: customer.email,
        identificationNumber: customer.identificationNumber,
        registrationDate: customer.registrationDate
          ? dayjs(customer.registrationDate)
          : null,
        note: customer.note,
        partnerId: customer.partnerId,
        commissionRateForPartner: customer.commissionRateForPartner || 0,
      });
      setSelectedPartnerId(customer.partnerId || null);
      fetchPartners();
    }
  }, [open, customer, form]);

  const fetchPartners = async () => {
    try {
      setLoadingPartners(true);
      const data = await partnerService.getAllPartners();
      setPartners(data);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách đối tác:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  const handlePartnerChange = (value: number | null) => {
    setSelectedPartnerId(value);
    if (!value) {
      form.setFieldValue('commissionRateForPartner', 0);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();

      const formData: CreateCustomerRequest = {
        name: values.name,
        contactInfo: values.contactInfo,
        email: values.email,
        identificationNumber: values.identificationNumber,
        registrationDate: values.registrationDate?.format('YYYY-MM-DD') || '',
        note: values.note,
        partnerId: values.partnerId,
        commissionRateForPartner: values.partnerId
          ? values.commissionRateForPartner || 0
          : 0,
      };

      await customerService.editCustomer(customer.id, formData);
      notification.success({
        message: 'Cập nhật khách hàng thành công',
      });
      setOpen(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error updating customer:', error);
      notification.error({
        message: 'Lỗi khi cập nhật khách hàng',
        description: error.response?.data || 'Vui lòng thử lại.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Sửa">
        <Button
          type="text"
          icon={<EditOutlined style={{ fontSize: '18px' }} />}
          onClick={() => setOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className="w-8 h-8"
        />
      </Tooltip>

      <Modal
        title="Chỉnh sửa khách hàng"
        open={open}
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="back" onClick={() => setOpen(false)}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={handleSubmit}
          >
            Cập nhật
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Tên khách hàng"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên khách hàng' },
                ]}
              >
                <Input placeholder="Nhập tên khách hàng" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="contactInfo"
                label="Số điện thoại"
                rules={[
                  {
                    pattern: /^[0-9]+$/,
                    message: 'Vui lòng nhập số điện thoại hợp lệ',
                  },
                ]}
              >
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  {
                    type: 'email',
                    message: 'Vui lòng nhập đúng định dạng email',
                  },
                ]}
              >
                <Input placeholder="Nhập địa chỉ email" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="identificationNumber" label="Số CCCD/CMT">
                <Input placeholder="Nhập số CCCD/CMT" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="partnerId" label="Đối tác">
                <Select
                  allowClear
                  showSearch
                  placeholder="Chọn đối tác"
                  loading={loadingPartners}
                  optionFilterProp="children"
                  onChange={handlePartnerChange}
                  filterOption={(input, option) =>
                    (option?.label?.toString().toLowerCase() || '').includes(
                      input.toLowerCase()
                    )
                  }
                  options={partners.map((partner) => ({
                    value: partner.id,
                    label: partner.name,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="registrationDate" label="Ngày đăng ký">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày đăng ký"
                />
              </Form.Item>
            </Col>

            {selectedPartnerId && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="commissionRateForPartner"
                  label="Phần trăm hoa hồng (%)"
                  initialValue={0}
                  rules={[
                    {
                      type: 'number',
                      min: 0,
                      max: 100,
                      message: 'Phần trăm hoa hồng phải từ 0 đến 100%',
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    style={{ width: '100%' }}
                    placeholder="Nhập phần trăm hoa hồng"
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <Form.Item name="note" label="Ghi chú">
                <TextArea rows={3} placeholder="Nhập ghi chú" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default EditCustomerModal;
